import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { BottomSheet, KpiCard, RevenueChart } from '@evi/ui';
import { useLocationSummary } from '@/hooks/useLocationSummary';
import { useViewport } from '@/hooks/useViewport';
import { useAuthStore } from '@/store/auth';
import { listAgentPayouts } from '@/lib/api';
import { commissionCurrencies, commissionTotals, formatCommissionTotals } from '@/lib/spCommissionMoney';
import { type InfoCardLocation } from '@/components/LocationInfoCard';
import styles from './SalesPartnerLocationInfoCard.module.css';

/**
 * Phase 7g.4b.2.5 , Sales Partner per-location info card.
 * Phase 7g.4b.3 Commit 2 , commission KPI tile lands.
 *
 * Renders inside the SP dashboard's satellite map when the SP
 * clicks a location marker. Mirror of the operator's LocationInfoCard
 * chrome (panel-slide on desktop, BottomSheet on mobile) with
 * SP-flavored content:
 *   - Status badge (online / partial / faulted / idle) derived
 *     from chargerCount + onlineChargerCount (same shape as
 *     operator card).
 *   - Three KPI tiles: Commission earned (lifetime at this
 *     location), Chargers Online, Active Sessions. Operator card
 *     has four (Revenue Today, Energy MTD, Chargers Online,
 *     Active Sessions); SP card swaps the revenue/energy framing
 *     for SP-business framing.
 *   - Commission KPI: sums gross_commission_cents from the SP's
 *     listAgentPayouts response, filtered to rows whose
 *     location_id matches the selected location. Grouping by
 *     location_id (not name) defends against two locations in
 *     different cities sharing a name. Powered by Phase 7g.4b.3
 *     Commit 2's agent-payouts.ts projection extension.
 *   - Session-revenue chart slot via useLocationSummary. SP-scoped
 *     per Phase 7g.4a (sessions on SP's chargers only). Title
 *     "SESSION REVENUE" framed as the activity SP's commission
 *     calculates from.
 *   - "Open location detail" CTA → /locations/:id. Phase 7g.4a
 *     scoped that route for SP (cross-tenant lookup via
 *     Charger.exists); the detail page renders SP-scoped data.
 *
 * Why fork rather than extend the operator LocationInfoCard
 * ──────────────────────────────────────────────────────────
 * The operator card's KPI labels + data sources diverge enough
 * from SP needs that a variant prop on LocationInfoCard would
 * bloat its surface area without proportional savings. The two
 * cards share the chrome pattern (panel/sheet split, status
 * badge, framer-motion presence, CTA shape) by mirroring the
 * code structure, not by import; LocationInfoCard remains
 * untouched. The InfoCardLocation type is the one shared surface
 * (re-imported here so both cards consume identical shapes).
 */

type Props = {
  location: InfoCardLocation;
  onClose: () => void;
  onOpenDetail: (id: string) => void;
};

export function SalesPartnerLocationInfoCard({
  location, onClose, onOpenDetail,
}: Props) {
  const viewport = useViewport();
  const isNarrow = viewport === 'mobile' || viewport === 'tablet';

  const status = deriveStatus(location);

  // useLocationSummary is SP-scoped automatically per Phase 7g.4a.
  // The endpoint at apps/api/src/routes/locations/summary.ts routes
  // through resolveDashboardScope which derives chargerIds from
  // Charger.find({ agent_id: linkedAgentId }) for sales_agent
  // callers. Result: revenueLast24h represents revenue from
  // sessions at this location on SP's chargers, not whole-site
  // revenue. Same shape as operator data; SP-honest semantic.
  //
  // Period locked to 7d in v1. Could surface chips here for
  // 24h/7d/30d once the use case justifies; for now the SP card
  // is a quick-glance read, not a configurable surface.
  const summaryQ = useLocationSummary(location._id, '7d');

  // Phase 7g.4b.3 Commit 2 , per-location commission. Shares the
  // exact React Query key shape the SP Dashboard uses (line 119 of
  // SalesPartnerDashboardPage.tsx) so the two components dedupe to
  // a single fetch when both render in the same mount cycle. The
  // Dashboard mounts the card via DashboardMapBackground's
  // AnimatePresence, so the Dashboard's payouts query is always
  // primed by the time this card hydrates.
  const agentId = useAuthStore((s) => s.user?.agent_id ?? null);
  const payoutsQ = useQuery({
    queryKey: ['sp-dashboard', 'payouts', agentId],
    queryFn: () => {
      if (!agentId) throw new Error('agentId required');
      return listAgentPayouts(agentId);
    },
    enabled: !!agentId,
    staleTime: 30_000,
  });

  // Commission earned at this location = sum of gross_commission_cents
  // across all payout rows where location_id matches the selected
  // location's _id. Group by stable id, not by name, since two
  // locations in different cities can share a name. Phase 7g.4b.3
  // Commit 2's agent-payouts.ts projection ships location_id
  // alongside the display fields specifically for this grouping.
  // Lifetime sum (no window filter); SPs reading a per-location
  // card want "how much have I made here" without a date scope.
  // One amount per currency at this location, never a sum across two.
  const commissionAtLocation = useMemo(() => {
    if (!payoutsQ.data) return null;
    const rows = payoutsQ.data.rows.filter((r) => r.location_id === location._id);
    return formatCommissionTotals(commissionTotals(rows), commissionCurrencies(rows, null));
  }, [payoutsQ.data, location._id]);

  // ── Shared content (identical across panel + sheet) ─────────
  const statusBadge = (
    <span className={`${styles.statusBadge} ${statusClass(status)}`}>
      <span className={styles.statusDot} />
      {statusLabel(status)}
    </span>
  );

  const note = (
    <p className={styles.note}>
      Activity from sessions on your assigned chargers at this
      location. Open the location detail for the full charger list
      and address.
    </p>
  );

  // Two-tile KPI grid. Operator card has 4; SP drops the two
  // revenue/energy tiles whose framing reads as operator-business.
  // Chargers Online uses the InfoCardLocation counts (already
  // SP-scoped via the map-points endpoint per 7g.4a). Active
  // Sessions falls back to the summary endpoint's count.
  const kpiGrid = (
    <div className={styles.kpiGrid}>
      {/* Phase 7g.4b.3 , Commission at this location. Unblocked by
          Commit 2's agent-payouts.ts projection extension which
          shipped location_id on every AgentOpsQueueRow. Lifetime
          sum (no window filter) so SPs see the full historical
          contribution from this site. Em-dash while payouts are
          loading; No commission yet once the query resolves with zero matches. */}
      <KpiCard
        label="Commission earned"
        value={commissionAtLocation ?? 'Not available'}
        meta={commissionAtLocation == null ? undefined : 'lifetime'}
      />
      <KpiCard
        label="Chargers Online"
        value={`${location.onlineChargerCount} / ${location.chargerCount}`}
      />
      <KpiCard
        label="Active Sessions"
        value={summaryQ.data ? String(summaryQ.data.activeSessions) : 'Not available'}
        meta={summaryQ.data ? 'now' : undefined}
      />
    </div>
  );

  // Chart slot. Hidden while the first summary fetch is in flight
  // (no buckets to show) and on fatal error (the placeholder note
  // already explains the state). RevenueChart handles its own
  // all-zero empty branch internally so locations with no activity
  // still render cleanly.
  const chartSlot = summaryQ.data && !summaryQ.isError ? (
    <div className={styles.chartSlot}>
      <RevenueChart
        data={summaryQ.data.revenueLast24h}
        currentRate={0}
        period="7d"
        onPeriodChange={() => { /* locked to 7d in v1 */ }}
        allowedPeriods={['7d']}
        showRate={false}
        titleLabel="SESSION REVENUE"
      />
    </div>
  ) : null;

  // Phase 7g.4b.3 Commit 2 , the placeholder note is replaced by the
  // Commission KPI tile in the KPI grid above. Remaining slot kept
  // as a no-op so the panel + sheet layout below still receives
  // the prop and renders consistently across both presentations;
  // wires through as `null` so React skips it. A future phase
  // surfaces a commission-per-day chart here.
  const commissionNote: ReactNode = null;

  const cta = (
    <button
      type="button"
      className={styles.cta}
      onClick={() => onOpenDetail(location._id)}
    >
      Open location detail
      <ExternalLink size={14} />
    </button>
  );

  if (isNarrow) {
    return (
      <SheetPresentation
        title={location.name}
        onClose={onClose}
        statusBadge={statusBadge}
        note={note}
        kpiGrid={kpiGrid}
        chartSlot={chartSlot}
        commissionNote={commissionNote}
        cta={cta}
      />
    );
  }

  return (
    <PanelPresentation
      location={location}
      onClose={onClose}
      statusBadge={statusBadge}
      note={note}
      kpiGrid={kpiGrid}
      chartSlot={chartSlot}
      commissionNote={commissionNote}
      cta={cta}
    />
  );
}

/**
 * Desktop panel. Right-edge slide-in with ESC + click-outside
 * dismissal. Mirrors LocationInfoCard's PanelPresentation
 * structure verbatim; only the body content differs (handled by
 * the slot props from the parent).
 */
function PanelPresentation({
  location, onClose, statusBadge, note, kpiGrid, chartSlot, commissionNote, cta,
}: {
  location: InfoCardLocation;
  onClose: () => void;
  statusBadge: ReactNode;
  note: ReactNode;
  kpiGrid: ReactNode;
  chartSlot: ReactNode;
  commissionNote: ReactNode;
  cta: ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Click-outside closes , marker clicks excluded so a marker tap
  // doesn't race with the parent's selectedId swap. Same hit-test
  // hook (.location-marker-wrapper) the operator card uses.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (cardRef.current?.contains(target)) return;
      if (target instanceof HTMLElement && target.closest('.location-marker-wrapper')) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <motion.div
      ref={cardRef}
      className={styles.card}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 200 }}
      role="dialog"
      aria-label={`${location.name} details`}
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.name}>{location.name}</h3>
          <div className={styles.statusRow}>{statusBadge}</div>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      <div className={styles.body}>
        {note}
        {kpiGrid}
        {chartSlot}
        {commissionNote}
        {cta}
      </div>
    </motion.div>
  );
}

/**
 * Mobile/tablet presentation via @evi/ui BottomSheet which portals
 * to document.body. Sheet owns title, close, ESC, backdrop, swipe-
 * down, focus trap, body-scroll lock. We just supply the body
 * content.
 */
function SheetPresentation({
  title, onClose, statusBadge, note, kpiGrid, chartSlot, commissionNote, cta,
}: {
  title: string;
  onClose: () => void;
  statusBadge: ReactNode;
  note: ReactNode;
  kpiGrid: ReactNode;
  chartSlot: ReactNode;
  commissionNote: ReactNode;
  cta: ReactNode;
}) {
  return (
    <BottomSheet isOpen onClose={onClose} title={title}>
      <div className={styles.sheetBody}>
        <div className={styles.sheetStatusRow}>{statusBadge}</div>
        {note}
        {kpiGrid}
        {chartSlot}
        {commissionNote}
        <div className={styles.sheetCta}>{cta}</div>
      </div>
    </BottomSheet>
  );
}

// ── Status derivation (mirror of LocationInfoCard helpers) ──

type LocationStatus = 'online' | 'partial' | 'faulted' | 'idle';

function deriveStatus(loc: InfoCardLocation): LocationStatus {
  if (loc.chargerCount === 0) return 'idle';
  if (loc.onlineChargerCount === 0) return 'faulted';
  if (loc.onlineChargerCount < loc.chargerCount) return 'partial';
  return 'online';
}

function statusClass(s: LocationStatus): string {
  // CSS module class lookups fall back to the muted variant so
  // any module-name mismatch degrades visually rather than blowing
  // up. Same defensive pattern AgentStatusBadge uses.
  const fallback = styles.statusBadgeIdle ?? '';
  switch (s) {
    case 'online':  return styles.statusBadgeOnline ?? fallback;
    case 'partial': return styles.statusBadgeOffline ?? fallback;
    case 'faulted': return styles.statusBadgeFaulted ?? fallback;
    case 'idle':    return styles.statusBadgeIdle ?? fallback;
  }
}

function statusLabel(s: LocationStatus): string {
  switch (s) {
    case 'online':  return 'All online';
    case 'partial': return 'Partially online';
    case 'faulted': return 'Faulted';
    case 'idle':    return 'No chargers';
  }
}

